import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, lockScroll, unlockScroll, mediaQueryLarge } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLFormElement} form - The search form.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */

// Rank only the candidates returned by Shopify. Preserve the existing result cards,
// keyboard navigation and native full-search destination.
function rankReevysSearchResults(container, query) {
  const list = container.querySelector('.predictive-search-results__wrapper-products');
  if (!list) return;
  const normalized = query.toLowerCase().replace(/[-_]+/g, ' ').trim();
  const tokens = normalized.split(/\s+/).filter(Boolean);
  const rules = [
    { re: /wedding|bridesmaid/, occasion: 'wedding_guest' },
    { re: /date|dinner|date night/, occasion: 'dinner_date' },
    { re: /office|work/, occasion: 'work_office' },
    { re: /vacation|resort|holiday/, occasion: 'vacation_resort' },
    { re: /party|cocktail|going out|night out/, occasion: 'party_cocktail' }
  ];
  const colors = ['black', 'white', 'cream', 'beige', 'red', 'blue', 'navy', 'pink', 'green', 'olive', 'brown', 'burgundy', 'grey', 'gray', 'purple', 'orange', 'yellow'];
  const category =
    /dress(es)?/.test(normalized) ? 'dress' :
    /tops?|blouse|shirt|bodysuit/.test(normalized) ? 'top' :
    /pants?|trouser/.test(normalized) ? 'pant' :
    /skirts?/.test(normalized) ? 'skirt' :
    /shorts?|skort/.test(normalized) ? 'short' :
    /jackets?|blazer|coat/.test(normalized) ? 'outer' :
    /bags?|handbag/.test(normalized) ? 'bag' :
    /shoes?|heel|boot|sandal/.test(normalized) ? 'shoe' : null;
  const style =
    /romantic|feminine/.test(normalized) ? 'romantic' :
    /minimal|clean/.test(normalized) ? 'minimal' :
    /tailor|polished/.test(normalized) ? 'tailored' :
    /sexy|sensual/.test(normalized) ? 'sensual' : null;
  const silhouette =
    /midi/.test(normalized) ? 'midi' :
    /mini/.test(normalized) ? 'mini' :
    /maxi/.test(normalized) ? 'maxi' : null;
  const occasion = rules.find(({ re }) => re.test(normalized))?.occasion;
  const color = colors.find(value => tokens.includes(value));
  const material = ['linen', 'cotton', 'silk', 'satin', 'leather', 'denim', 'knit', 'wool'].find(value => tokens.includes(value));
  if (!occasion && !color && !category && !material && !style && !silhouette) return;
  const cards = Array.from(list.children);
  const ranked = cards.map((card, index) => {
    const data = card.dataset;
    const title = card.textContent.toLowerCase();
    let score = 0;
    const occasionValues = (data.reevysOccasion || '').split('|');
    const occasionAliases = {
      work_office: ['work_office', 'work'],
      party_cocktail: ['party_cocktail', 'going_out'],
      dinner_date: ['dinner_date', 'date_night']
    };
    if (occasion && (occasionAliases[occasion] || [occasion]).some(value => occasionValues.includes(value))) score += 10;
    if (category && (data.reevysCategory || '').includes(category)) score += 8;
    if (color && (data.reevysColor || '').split('|').includes(color)) score += 7;
    if (material && (data.reevysMaterial || '').includes(material)) score += 5;
    if (style && (data.reevysStyle || '').includes(style)) score += 4;
    if (silhouette && ((data.reevysSilhouette || '').includes(silhouette) || title.includes(silhouette))) score += 4;
    if ((data.reevysMerchRole || '').includes('hero')) score += 1;
    if (tokens.every(token => title.includes(token))) score += 3;
    return { card, index, score };
  });
  ranked.sort((a, b) => b.score - a.score || a.index - b.index);
  list.replaceChildren(...ranked.map(item => item.card));
}

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;
  #inertSiblings = [];
  #closingHeader = false;
  #restoringHeaderFocus = false;

  get isInline() {
    return this.classList.contains('reevys-header-search');
  }

  #closeInlineResults = () => {
    this.removeAttribute('data-results-open');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
  };

  #openInlineResults = () => {
    if (!this.isInline || !this.hasAttribute('data-drawer-open') || !this.refs.predictiveSearchResults.childElementCount) return;
    this.setAttribute('data-results-open', '');
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  };

  #openHeaderSearch = () => {
    if (!mediaQueryLarge.matches || this.#closingHeader || this.#restoringHeaderFocus || this.hasAttribute('data-drawer-open')) return;
    this.setAttribute('data-drawer-open', '');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    lockScroll(this);
    // Keep the existing input and component in place; only the visual geometry changes.
    let branch = this;
    while (branch.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && sibling instanceof HTMLElement && !sibling.inert) {
          sibling.inert = true;
          this.#inertSiblings.push(sibling);
        }
      }
      branch = branch.parentElement;
    }
    trapFocus(this.refs.form);
    this.#openInlineResults();
    if (!this.refs.searchInput.value.trim() && !this.refs.predictiveSearchResults.childElementCount) this.#loadHeaderEmptyState();
  };

  closeHeaderSearch = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!this.hasAttribute('data-drawer-open') || this.#closingHeader) return;
    this.#closingHeader = true;
    this.#activeFetch?.abort();
    this.setAttribute('data-drawer-closing', '');
    if (mediaQueryLarge.matches && !prefersReducedMotion()) {
      await onAnimationEnd(this.refs.form, undefined, { subtree: false });
    }
    removeTrapFocus();
    for (const sibling of this.#inertSiblings) sibling.inert = false;
    this.#inertSiblings = [];
    this.removeAttribute('data-drawer-open');
    this.removeAttribute('data-drawer-closing');
    this.removeAttribute('aria-modal');
    this.setAttribute('role', 'search');
    this.#closeInlineResults();
    unlockScroll(this);
    this.#restoringHeaderFocus = true;
    if (mediaQueryLarge.matches) this.refs.searchInput.focus({ preventScroll: true });
    else this.refs.searchInput.blur();
    this.#restoringHeaderFocus = false;
    this.#closingHeader = false;
  };

  #handleHeaderBreakpoint = () => {
    if (!mediaQueryLarge.matches) this.closeHeaderSearch();
  };

  #renderResults(markup) {
    const results = this.refs.predictiveSearchResults;
    if (this.isInline) {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const prefix = `${this.refs.searchInput.id}-`;
      for (const element of parsed.querySelectorAll('[id]')) element.id = prefix + element.id;
      for (const element of parsed.querySelectorAll('[aria-controls], [aria-labelledby], [for]')) {
        for (const attribute of ['aria-controls', 'aria-labelledby', 'for']) {
          const value = element.getAttribute(attribute);
          if (value) element.setAttribute(attribute, value.split(' ').map(id => prefix + id).join(' '));
        }
      }
      markup = parsed.body.innerHTML;
    }
    morph(results, markup);
    if (this.refs.searchInput.value.trim()) rankReevysSearchResults(results, this.refs.searchInput.value.trim().toLowerCase());
    if (this.isInline && this.hasAttribute('data-drawer-open')) {
      this.#openInlineResults();
      trapFocus(this.refs.form);
    }
  }

  async #loadHeaderEmptyState() {
    const controller = this.#createAbortController();
    this.refs.predictiveSearchResults.setAttribute('aria-busy', 'true');
    try {
      const markup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, new URL(Theme.routes.search_url, location.origin));
      if (!controller.signal.aborted && markup) this.#renderResults(markup);
    } catch (error) {
      if (!controller.signal.aborted) console.warn('Unable to load search suggestions', error);
    } finally {
      this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    }
  }

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.isInline) {
      this.refs.searchInput.addEventListener('focus', this.#openHeaderSearch, { signal });
      this.refs.searchInput.addEventListener('click', this.#openHeaderSearch, { signal });
      mediaQueryLarge.addEventListener('change', this.#handleHeaderBreakpoint, { signal });
    }

    if (!this.isInline && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.isInline) {
      this.#activeFetch?.abort();
      if (this.hasAttribute('data-drawer-open')) removeTrapFocus();
      for (const sibling of this.#inertSiblings) sibling.inert = false;
      unlockScroll(this);
    }
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (this.isInline && event.key === 'Escape') {
      this.closeHeaderSearch(event);
      return;
    }
    if (this.isInline && event.key === 'Tab') return;
    if (this.isInline && event.key === 'Enter' && this.#currentIndex < 0) return;
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType && !this.isInline) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'query,product,page,article');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        this.#renderResults(resultsMarkup);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    if (this.isInline) {
      this.#activeFetch?.abort();
      predictiveSearchResults.replaceChildren();
      this.#closeInlineResults();
      if (this.hasAttribute('data-drawer-open')) this.#loadHeaderEmptyState();
      return;
    }

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
